import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Eye, Send } from "lucide-react";

const ITEMS = [
  { label: "Accommodation — Water Villa, 6 nights", amount: 120000 },
  { label: "Private transfers", amount: 18000 },
  { label: "Activities — cruise, island hopping, spa", amount: 25000 },
  { label: "Taxes & fees", amount: 12000 },
  { label: "Honeymoon discount", amount: -5000 },
];
const inr = (n) => `₹${Math.abs(n).toLocaleString("en-IN")}`;

export default function SimQuote() {
  const [view, setView] = useState("editor");
  const [responded, setResponded] = useState(null);
  const total = ITEMS.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="sim-quote">
      <div className="rounded-2xl border border-sand bg-white p-5 sm:p-6">
        <p className="mb-1 flex items-center justify-between font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-stone2">
          Quotation · DMC side <span className="rounded-full bg-parch px-2 py-0.5 text-[9px] normal-case tracking-normal">sample data</span>
        </p>
        <p className="font-serif text-xl text-ink">Maldives Escape</p>
        <p className="mb-4 text-xs text-stone2">2 Adults + 1 Child · 12–18 February · INQ-2027-00482</p>
        <ul className="space-y-2">
          {ITEMS.map((i) => (
            <li key={i.label} className="flex items-center justify-between border-b border-dashed border-sand pb-2 text-sm">
              <span className="text-stone2">{i.label}</span>
              <span className={`font-mono font-semibold ${i.amount < 0 ? "text-emerald2" : "text-ink"}`}>
                {i.amount < 0 ? "−" : ""}{inr(i.amount)}
              </span>
            </li>
          ))}
          <li className="flex items-center justify-between pt-1">
            <span className="font-serif text-lg text-ink">Total</span>
            <span className="font-serif text-2xl text-ink" data-testid="sim-quote-total">{inr(total)}</span>
          </li>
        </ul>
        <div className="mt-5 flex flex-wrap gap-2">
          <button onClick={() => setView("customer")} data-testid="sim-quote-preview-btn"
            className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-xs font-bold text-ink transition-colors hover:bg-ink hover:text-paper">
            <Eye className="h-3.5 w-3.5" /> Preview Quote
          </button>
          <button onClick={() => { setView("customer"); setResponded(null); }} data-testid="sim-quote-send-btn"
            className="inline-flex items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-xs font-bold text-ink transition-colors hover:bg-ink hover:text-paper">
            <Send className="h-3.5 w-3.5" /> Send to Customer
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {view === "customer" ? (
          <motion.div key="cust" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="overflow-hidden rounded-2xl border border-sand bg-ink text-paper" data-testid="sim-quote-customer-view">
            <div className="relative h-28">
              <img src="https://images.unsplash.com/photo-1743356174397-d6da6f014f8f?auto=format&fit=crop&w=900&q=70" alt="Maldives" loading="lazy" className="h-full w-full object-cover opacity-70" />
              <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-ink to-transparent p-4">
                <p className="font-serif text-xl">Your Maldives Journey</p>
                <p className="text-[11px] text-paper/70">6 Nights / 7 Days · prepared by your travel specialist</p>
              </div>
            </div>
            <div className="p-5">
              <div className="flex flex-wrap gap-1.5 text-[10px] font-semibold">
                {["Water Villa", "Half Board", "Private Transfers", "3 Experiences", "Valid 7 days"].map((c) => (
                  <span key={c} className="rounded-full bg-white/10 px-2.5 py-1">{c}</span>
                ))}
              </div>
              <div className="mt-4 flex items-end justify-between border-t border-white/10 pt-4">
                <span className="text-xs text-paper/60">Total for 2 adults + 1 child</span>
                <span className="font-serif text-3xl text-terra">{inr(total)}</span>
              </div>
              {responded ? (
                <motion.p initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="mt-4 flex items-center gap-2 rounded-lg bg-emerald2/20 px-3.5 py-2.5 text-xs font-semibold text-emerald-200" data-testid="sim-quote-response-note">
                  <Check className="h-4 w-4" /> {responded} — captured in the DMC's dashboard. Online payment and booking arrive in a future phase.
                </motion.p>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => setResponded("Proposal accepted")} data-testid="sim-quote-accept-btn"
                    className="flex-1 rounded-full bg-terra py-3 text-xs font-bold text-ink transition-colors hover:bg-paper">
                    Accept Proposal
                  </button>
                  <button onClick={() => setResponded("Changes requested")} data-testid="sim-quote-changes-btn"
                    className="flex-1 rounded-full border border-white/25 py-3 text-xs font-bold text-paper transition-colors hover:bg-white/10">
                    Request Changes
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div key="placeholder" exit={{ opacity: 0 }}
            className="flex min-h-[260px] flex-col items-center justify-center rounded-2xl border border-dashed border-sand bg-paper/60 p-6 text-center">
            <Eye className="mb-3 h-6 w-6 text-stone2/50" />
            <p className="max-w-xs text-sm text-stone2">Preview the quote to see exactly what your customer receives.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
